import React, { useMemo, useState } from 'react';
import type {
  CrAdVariant,
  CrFunnelStage,
  CrFunnelTone,
  CrKeywordRow,
  CrLogEntry,
  CrTab,
} from '../../growth-campaign-report/types';
import {
  CR_AD_VARIANTS,
  CR_BOOKINGS_BY_DAY,
  CR_FUNNEL,
  CR_HERO_METRICS,
  CR_KEYWORDS,
  CR_LOG,
  CR_PATIENT_VALUE,
  CR_PEAK_HOURS,
} from '../../growth-campaign-report/mockData';
import {
  CR_LOG_TYPES,
  CR_TABS,
  GROWTH_CAMPAIGN_REPORT_META,
} from '../../growth-campaign-report/config';
import { Icon } from '../Icon/Icon';
import { Segmented } from '../Segmented/Segmented';
import './growth-campaign-report.css';

export interface CampaignReportData {
  heroMetrics?: typeof CR_HERO_METRICS;
  funnel?: typeof CR_FUNNEL;
  bookingsByDay?: typeof CR_BOOKINGS_BY_DAY;
  peakHours?: typeof CR_PEAK_HOURS;
  patientValue?: typeof CR_PATIENT_VALUE;
  keywords?: typeof CR_KEYWORDS;
  adVariants?: typeof CR_AD_VARIANTS;
  log?: typeof CR_LOG;
}

export interface CampaignReportViewProps {
  /** Route param identifying the campaign being reported on. */
  campaignId?: string;
  data?: CampaignReportData;
  loading?: boolean;
  onPause?: () => void;
  onExport?: () => void;
  onBack?: () => void;
}

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

const FUNNEL_TONE: Record<CrFunnelTone, string> = {
  neutral: 'var(--ink-4)',
  info: 'var(--info)',
  gold: 'var(--gold)',
  success: 'var(--success)',
};

const KEYWORD_STATUS: Record<
  CrKeywordRow['status'],
  { label: string; accent: string; tint: string }
> = {
  top: { label: '★ Top performer', accent: 'var(--success)', tint: 'var(--success-tint)' },
  good: { label: 'Active', accent: 'var(--success)', tint: 'var(--success-tint)' },
  watch: { label: 'Watching', accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  paused: { label: 'Paused by ELVA', accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

function CrCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function CrSectionHead({
  icon,
  title,
  sub,
}: {
  icon: 'activity' | 'calendar' | 'clock' | 'dollar' | 'search' | 'doc' | 'clipboard';
  title: string;
  sub?: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon kind={icon} size={16} stroke={1.9} className="mt-0.5 shrink-0 text-[var(--ai-3)]" />
      <div>
        <h3 className="text-[15px] font-semibold text-[var(--ink-1)]">{title}</h3>
        {sub ? <p className="mt-1 text-[12.5px] text-[var(--ink-4)]">{sub}</p> : null}
      </div>
    </div>
  );
}

function CrFunnelTile({ stage }: { stage: CrFunnelStage }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className={eyebrowClass}>{stage.label}</div>
      <div className="mt-2 font-[var(--font-display)] text-[22px] font-bold tracking-[-0.03em] text-[var(--ink-0)]">
        {stage.value.toLocaleString()}
      </div>
      <div className="mx-auto mt-3 flex h-8 w-full items-center justify-center rounded-[8px]" style={{ width: `${stage.width}%`, minWidth: 44, background: FUNNEL_TONE[stage.tone] }}>
        {stage.note ? (
          <span className="text-[11px] font-semibold text-[var(--cream-1)]">{stage.note}</span>
        ) : null}
      </div>
      {!stage.isLast && stage.note ? (
        <div className="mt-1.5 text-[11px] text-[var(--ink-4)]">↓ {stage.note}</div>
      ) : null}
    </div>
  );
}

function CrBar({
  label,
  value,
  max,
  note,
  labelWidth,
}: {
  label: string;
  value: number;
  max: number;
  note?: string;
  labelWidth: number;
}) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2.5">
      <span
        className="shrink-0 text-right text-[12px] text-[var(--ink-3)]"
        style={{ width: labelWidth }}
      >
        {label}
      </span>
      <div className="h-5 flex-1 overflow-hidden rounded-[var(--r-sm)] bg-[var(--surface-mute)]">
        <div
          className="flex h-full items-center rounded-[var(--r-sm)] px-2 text-[11px] font-semibold text-[var(--cream-1)]"
          style={{
            width: `${pct}%`,
            background: value >= max * 0.66 ? 'var(--success)' : value > 0 ? 'color-mix(in srgb, var(--success) 55%, var(--cream-1))' : 'var(--surface-mute)',
          }}
        >
          {value > 0 ? value : null}
        </div>
      </div>
      {note ? <span className="w-[80px] shrink-0 text-[11px] text-[var(--ink-4)]">{note}</span> : null}
    </div>
  );
}

function CrKeywordTable({ rows }: { rows: CrKeywordRow[] }) {
  const headers = ['Keyword', 'Match', 'Impressions', 'Clicks', 'CPC', 'Bookings', 'Cost/Booking', 'Status'];
  return (
    <div className="cr-table-wrap overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-[13px]">
        <thead>
          <tr>
            {headers.map((header) => (
              <th
                key={header}
                className={[
                  'border-b border-[var(--cream-2)] bg-[var(--surface-mute)] px-3.5 py-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[var(--ink-3)]',
                  header === 'Keyword' || header === 'Match' || header === 'Status'
                    ? 'text-left'
                    : 'text-center',
                ].join(' ')}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const status = KEYWORD_STATUS[row.status];
            return (
              <tr key={row.keyword} style={{ opacity: row.status === 'paused' ? 0.5 : 1 }}>
                <td className="border-b border-[var(--cream-2)] px-3.5 py-3 font-medium text-[var(--ink-1)]">
                  {row.keyword}
                </td>
                <td className="border-b border-[var(--cream-2)] px-3.5 py-3">
                  <span className="inline-flex rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-2.5 py-1 text-[11px] font-semibold text-[var(--ink-3)]">
                    {row.match}
                  </span>
                </td>
                <td className="border-b border-[var(--cream-2)] px-3.5 py-3 text-center font-[var(--font-mono)] text-[12px] text-[var(--ink-2)]">
                  {row.impressions.toLocaleString()}
                </td>
                <td className="border-b border-[var(--cream-2)] px-3.5 py-3 text-center font-[var(--font-mono)] text-[12px] text-[var(--ink-2)]">
                  {row.clicks}
                </td>
                <td className="border-b border-[var(--cream-2)] px-3.5 py-3 text-center font-[var(--font-mono)] text-[12px] text-[var(--ink-2)]">
                  ${row.cpc.toFixed(2)}
                </td>
                <td className="border-b border-[var(--cream-2)] px-3.5 py-3 text-center">
                  <span
                    className="font-[var(--font-display)] text-[16px] font-bold"
                    style={{
                      color:
                        row.bookings > 3
                          ? 'var(--success)'
                          : row.bookings > 0
                            ? 'var(--ink-1)'
                            : 'var(--ink-4)',
                    }}
                  >
                    {row.bookings || '—'}
                  </span>
                </td>
                <td className="border-b border-[var(--cream-2)] px-3.5 py-3 text-center">
                  {row.costPerBooking > 0 ? (
                    <span
                      className="font-[var(--font-display)] text-[14px] font-bold"
                      style={{ color: row.costPerBooking > 40 ? 'var(--gold)' : 'var(--success)' }}
                    >
                      ${row.costPerBooking.toFixed(0)}
                    </span>
                  ) : (
                    <span className="text-[var(--ink-4)]">—</span>
                  )}
                </td>
                <td className="border-b border-[var(--cream-2)] px-3.5 py-3">
                  <span
                    className="inline-flex rounded-[var(--r-pill)] px-2.5 py-1 text-[11px] font-semibold"
                    style={{ background: status.tint, color: status.accent }}
                  >
                    {status.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CrAdCard({ ad }: { ad: CrAdVariant }) {
  const metrics = [
    { label: 'Impressions', value: ad.impressions.toLocaleString() },
    { label: 'Clicks', value: String(ad.clicks) },
    { label: 'CTR', value: `${ad.ctr}%` },
    { label: 'Bookings', value: String(ad.bookings) },
    { label: 'Cost/Patient', value: `$${ad.costPerBooking.toFixed(0)}` },
  ];
  return (
    <div
      className="rounded-[var(--r-md)] p-5"
      style={{
        border: ad.winner
          ? '1px solid color-mix(in srgb, var(--success) 45%, transparent)'
          : '1px solid var(--cream-2)',
        background: ad.winner ? 'var(--success-tint)' : 'var(--cream-1)',
      }}
    >
      <div className="mb-3 flex items-center gap-2">
        <span className="text-[13px] font-semibold text-[var(--ink-2)]">{ad.label}</span>
        <span
          className="inline-flex rounded-[var(--r-pill)] px-2.5 py-1 text-[11px] font-semibold"
          style={{
            background: ad.winner ? 'var(--success-tint)' : 'var(--surface-mute)',
            color: ad.winner ? 'var(--success)' : 'var(--ink-3)',
          }}
        >
          {ad.winner ? `★ Winner — ${ad.share} traffic` : `${ad.share} traffic`}
        </span>
      </div>
      <div className="mb-3.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-[18px] py-3.5">
        <div className="mb-1 text-[11px] text-[var(--ink-4)]">AD · smiledentaldowntown.com</div>
        <div className="mb-1 font-[var(--font-display)] text-[16px] font-semibold text-[var(--info)]">
          {ad.headline}
        </div>
        <div className="text-[13px] leading-[1.6] text-[var(--ink-2)]">{ad.description}</div>
      </div>
      <div className="cr-ad-metrics grid gap-3" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
        {metrics.map((m) => (
          <div key={m.label} className="text-center">
            <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--ink-4)]">
              {m.label}
            </div>
            <span className="font-[var(--font-display)] text-[16px] font-bold text-[var(--ink-1)]">
              {m.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CrLogRow({ entry, isLast }: { entry: CrLogEntry; isLast: boolean }) {
  const def = CR_LOG_TYPES[entry.type];
  return (
    <div className="flex gap-4 py-[18px]" style={{ borderBottom: isLast ? 'none' : '1px solid var(--cream-2)' }}>
      <div className="flex w-[60px] shrink-0 flex-col items-center">
        <span className="font-[var(--font-mono)] text-[12px] font-semibold text-[var(--ink-3)]">
          {entry.date}
        </span>
        <span className="mt-2 h-2.5 w-2.5 rounded-full bg-[var(--ai-3)]" />
        {!isLast ? <span className="mt-1 w-px flex-1 bg-[var(--cream-2)]" /> : null}
      </div>
      <div className="flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="text-[14px] font-semibold text-[var(--ink-1)]">{entry.action}</span>
          <span className="inline-flex items-center gap-1 rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-2 py-0.5 text-[11px] font-semibold text-[var(--ink-3)]">
            <Icon kind={def.icon} size={12} stroke={2} />
            {def.label}
          </span>
        </div>
        <div className="mb-1.5 text-[13px] leading-[1.5] text-[var(--ink-3)]">{entry.reason}</div>
        <div className="inline-flex items-center gap-1.5 rounded-[var(--r-sm)] bg-[var(--success-tint)] px-2.5 py-1 text-[12px] font-semibold text-[var(--success)]">
          <Icon kind="check" size={12} stroke={2.5} />
          {entry.impact}
        </div>
      </div>
    </div>
  );
}

export function CampaignReportView({
  campaignId: _campaignId,
  data,
  loading = false,
  onPause,
  onExport,
  onBack,
}: CampaignReportViewProps = {}) {
  const meta = GROWTH_CAMPAIGN_REPORT_META;
  const [tab, setTab] = useState<CrTab>('overview');

  const connected = data !== undefined;
  const heroMetrics = connected ? (data.heroMetrics ?? []) : CR_HERO_METRICS;
  const funnel = connected ? (data.funnel ?? []) : CR_FUNNEL;
  const bookingsByDay = connected ? (data.bookingsByDay ?? []) : CR_BOOKINGS_BY_DAY;
  const peakHours = connected ? (data.peakHours ?? []) : CR_PEAK_HOURS;
  const patientValue = connected ? (data.patientValue ?? []) : CR_PATIENT_VALUE;
  const keywords = connected ? (data.keywords ?? []) : CR_KEYWORDS;
  const adVariants = connected ? (data.adVariants ?? []) : CR_AD_VARIANTS;
  const log = connected ? (data.log ?? []) : CR_LOG;

  const bookingsMax = useMemo(() => Math.max(...bookingsByDay.map((d) => d.value), 1), [bookingsByDay]);
  const hoursMax = useMemo(() => Math.max(...peakHours.map((h) => h.value), 1), [peakHours]);

  const tabOptions = CR_TABS.map((t) => ({ value: t.id, label: t.label }));

  return (
    <div className="growth-campaign-report-root relative mx-auto w-full max-w-[1320px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-[760px]">
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="activity" size={13} stroke={2} className="text-[var(--ai-3)]" />
            <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
              {meta.eyebrow}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="m-0 font-[var(--font-display)] text-[clamp(24px,3vw,31px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
              {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
            </h1>
            <span className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] border border-[color-mix(in_srgb,var(--success)_40%,transparent)] bg-[var(--success-tint)] px-2.5 py-1 text-[11px] font-semibold text-[var(--success)]">
              <Icon kind="bolt" size={11} stroke={2.4} />
              Autopilot
            </span>
            <span className="inline-flex rounded-[var(--r-pill)] bg-[var(--success-tint)] px-2.5 py-1 text-[11px] font-semibold text-[var(--success)]">
              Active
            </span>
          </div>
          <p className="mt-2.5 text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            {meta.description}
            {loading ? ' · Updating…' : ''}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={onPause}
            className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 text-[13px] font-semibold text-[var(--ink-2)]"
          >
            <Icon kind="pause" size={15} stroke={2} />
            Pause Campaign
          </button>
          <button
            type="button"
            onClick={onExport}
            className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-[var(--r-md)] border-0 px-4 text-[13px] font-semibold text-[var(--cream-1)] shadow-[var(--sh-1)]"
            style={{ background: 'var(--success)' }}
          >
            <Icon kind="download" size={15} stroke={2} />
            Export Report
          </button>
        </div>
      </div>

      <div className="cr-hero-grid mb-3 grid gap-3" style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}>
        {heroMetrics.map((metric) => (
          <CrCard key={metric.label} className="px-[18px] py-4">
            <div className={eyebrowClass}>{metric.label}</div>
            <div
              className="mt-2 font-[var(--font-display)] text-[24px] font-extrabold leading-none tracking-[-0.04em]"
              style={{ color: metric.positive ? 'var(--success)' : 'var(--ink-0)' }}
            >
              {metric.value}
            </div>
            <div
              className="mt-1.5 text-[11px] font-medium"
              style={{ color: metric.subPositive ? 'var(--success)' : 'var(--ink-4)' }}
            >
              {metric.sub}
            </div>
          </CrCard>
        ))}
      </div>

      <div className="mb-7 flex flex-wrap items-center gap-3 rounded-[var(--r-xl)] border border-[color-mix(in_srgb,var(--success)_26%,transparent)] bg-[var(--success-tint)] px-[22px] py-3.5">
        <Icon kind="dollar" size={20} stroke={2} className="shrink-0 text-[var(--success)]" />
        <span className="text-[14px] font-medium text-[var(--success)]">
          This campaign generated <strong className="text-[var(--ink-1)]">$8,400</strong> in revenue
          from <strong className="text-[var(--ink-1)]">$620</strong> in ad spend — that&apos;s a{' '}
          <strong className="text-[var(--ink-1)]">13.5x return</strong>. Each new emergency patient is
          worth an average of <strong className="text-[var(--ink-1)]">$382</strong> in their first
          visit.
        </span>
      </div>

      <div className="mb-7">
        <Segmented options={tabOptions} value={tab} onChange={(value) => setTab(value as CrTab)} />
      </div>

      {tab === 'overview' ? (
        <div className="flex flex-col gap-6">
          <CrCard className="p-6">
            <CrSectionHead
              icon="activity"
              title="Conversion Funnel"
              sub="From ad impression to booked appointment"
            />
            <div className="cr-funnel-grid mt-6 grid items-start gap-4" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
              {funnel.map((stage) => (
                <CrFunnelTile key={stage.id} stage={stage} />
              ))}
            </div>
          </CrCard>

          <div className="cr-two-col grid gap-4" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <CrCard className="p-6">
              <CrSectionHead icon="calendar" title="Bookings by Day of Week" />
              <div className="mt-4 flex flex-col gap-2">
                {bookingsByDay.map((row) => (
                  <CrBar key={row.day} label={row.day} value={row.value} max={bookingsMax} labelWidth={30} />
                ))}
              </div>
              <p className="mt-3.5 text-[12px] italic text-[var(--ink-4)]">
                Autopilot allocates more budget to Fri/Sat when booking rates peak
              </p>
            </CrCard>

            <CrCard className="p-6">
              <CrSectionHead icon="clock" title="Peak Booking Hours" />
              <div className="mt-4 flex flex-col gap-2">
                {peakHours.map((row) => (
                  <CrBar
                    key={row.slot}
                    label={row.slot}
                    value={row.value}
                    max={hoursMax}
                    note={row.note}
                    labelWidth={60}
                  />
                ))}
              </div>
              <p className="mt-3.5 text-[12px] italic text-[var(--ink-4)]">
                Autopilot increases bids during 8–10 AM and 4–6 PM windows
              </p>
            </CrCard>
          </div>

          <CrCard className="p-6">
            <CrSectionHead
              icon="dollar"
              title="Patient Value Breakdown"
              sub="Revenue attribution for appointments booked through this campaign"
            />
            <div className="cr-value-grid mt-4 grid gap-3" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
              {patientValue.map((item) => (
                <div key={item.label} className="rounded-[var(--r-md)] bg-[var(--surface-mute)] px-[18px] py-4">
                  <div className={eyebrowClass}>{item.label}</div>
                  <div className="mt-2 font-[var(--font-display)] text-[22px] font-bold tracking-[-0.03em] text-[var(--ink-0)]">
                    {item.value}
                  </div>
                  <div className="mt-1.5 text-[11px] text-[var(--ink-4)]">{item.sub}</div>
                </div>
              ))}
            </div>
          </CrCard>
        </div>
      ) : null}

      {tab === 'keywords' ? (
        <CrCard className="overflow-hidden">
          <div className="border-b border-[var(--cream-2)] px-6 py-5">
            <CrSectionHead
              icon="search"
              title="Keyword Performance"
              sub="Sorted by bookings generated — ELVA auto-adjusts bids based on conversion data"
            />
          </div>
          <CrKeywordTable rows={keywords} />
        </CrCard>
      ) : null}

      {tab === 'ads' ? (
        <CrCard className="p-6">
          <CrSectionHead
            icon="doc"
            title="Ad Variants"
            sub="ELVA continuously A/B tests ad copy and favors the best performer"
          />
          <div className="mt-5 flex flex-col gap-4">
            {adVariants.map((ad) => (
              <CrAdCard key={ad.label} ad={ad} />
            ))}
          </div>
        </CrCard>
      ) : null}

      {tab === 'log' ? (
        <CrCard className="p-6">
          <CrSectionHead
            icon="clipboard"
            title="ELVA Autopilot Change Log"
            sub="Every optimization ELVA made to this campaign, with reasoning and impact"
          />
          <div className="mt-2 flex flex-col">
            {log.map((entry, index) => (
              <CrLogRow key={entry.date} entry={entry} isLast={index === log.length - 1} />
            ))}
          </div>
        </CrCard>
      ) : null}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-[var(--cream-2)] pt-4">
        <span className="text-[12px] text-[var(--ink-4)]">
          Campaign running since March 12, 2026 · Reporting period: April 1–9, 2026
        </span>
        <button
          type="button"
          onClick={onBack}
          className="cursor-pointer rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-1.5 text-[13px] font-semibold text-[var(--ink-2)]"
        >
          ← Back to all campaigns
        </button>
      </div>
    </div>
  );
}
